import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatCzk, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import {
  ArrowLeft, Mail, Phone, MapPin, Heart, Wallet, ShieldCheck, Package, Gavel,
} from "lucide-react";

const AdminUserDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${id}`).then((r) => setData(r.data)).catch(() => {});
  }, [id]);

  if (!data) return (<div><Navbar /><div className="container-page py-16 text-center text-slate-500">Načítání…</div></div>);

  const isProvider = data.role === "provider";
  const isCustomer = data.role === "customer";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600">
          <ArrowLeft className="w-4 h-4" /> Zpět do admin přehledu
        </Link>

        <div className="mt-4 grid lg:grid-cols-3 gap-6">
          {/* Profile card */}
          <Card className="lg:col-span-1 p-7">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-600 text-white grid place-items-center font-display font-bold text-2xl">
                {data.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div>
                <h1 className="font-display text-xl font-bold text-slate-900">{data.name}</h1>
                <Badge variant="secondary" className="mt-1 capitalize">
                  {data.role === "customer" ? "Zákazník" : data.role === "provider" ? "Prádelna" : "Admin"}
                </Badge>
              </div>
            </div>

            <div className="mt-5 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <Mail className="w-4 h-4 text-slate-400" /> {data.email}
              </div>
              {data.phone && (
                <div className="flex items-center gap-2 text-slate-700">
                  <Phone className="w-4 h-4 text-slate-400" /> {data.phone}
                </div>
              )}
              {(data.city || data.address) && (
                <div className="flex items-start gap-2 text-slate-700">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    {data.address && <div>{data.address}</div>}
                    <div>{data.city} {data.zip}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Ověření</div>
                <div className="font-semibold text-slate-900">{data.verified ? "Ano" : "Ne"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Věrnost. body</div>
                <div className="font-semibold text-slate-900">{data.loyalty_points || 0}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 flex items-center gap-1"><Heart className="w-3 h-3" /> Daroval</div>
                <div className="font-semibold text-slate-900">{formatCzk(data.donations_given || 0)}</div>
              </div>
            </div>

            {isCustomer && data.customer_summary && (
              <div className="mt-5 pt-5 border-t border-slate-100 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Útrata celkem</span>
                  <span className="font-display font-bold text-slate-900">{formatCzk(data.customer_summary.total_spent)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Dokončené / aktivní / zrušené</span>
                  <span>{data.customer_summary.completed} / {data.customer_summary.active} / {data.customer_summary.cancelled}</span>
                </div>
              </div>
            )}

            {isProvider && data.payouts && (
              <div className="mt-5 pt-5 border-t border-slate-100 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Vyplaceno</span>
                  <span className="font-display font-bold text-emerald-700">{formatCzk(data.payouts.paid)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Čeká na výplatu</span>
                  <span className="font-display font-bold text-amber-700">{formatCzk(data.payouts.pending)}</span>
                </div>
                <div className="text-xs text-slate-400">
                  {data.payouts.count_paid} dokončených · {data.payouts.count_pending} v běhu
                </div>
              </div>
            )}

            {isProvider && data.provider_profile && (
              <div className="mt-5 pt-5 border-t border-slate-100 text-sm">
                <div className="font-semibold text-slate-900">{data.provider_profile.business_name}</div>
                {data.provider_profile.description && (
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{data.provider_profile.description}</p>
                )}
                <div className="text-xs text-slate-500 mt-2">
                  Kapacita: {data.provider_profile.capacity_per_week_kg} kg/týden ·
                  Hodnocení: {data.provider_profile.rating_count > 0 ? (data.provider_profile.rating_sum / data.provider_profile.rating_count).toFixed(1) : "—"}
                </div>
              </div>
            )}
          </Card>

          {/* Tabs with detail */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="orders">
              <TabsList>
                <TabsTrigger value="orders" data-testid="ud-tab-orders">
                  <Package className="w-3.5 h-3.5 mr-1.5" /> Objednávky ({data.orders.length})
                </TabsTrigger>
                {isProvider && (
                  <TabsTrigger value="payouts" data-testid="ud-tab-payouts">
                    <Wallet className="w-3.5 h-3.5 mr-1.5" /> Výplaty
                  </TabsTrigger>
                )}
                <TabsTrigger value="disputes" data-testid="ud-tab-disputes">
                  <Gavel className="w-3.5 h-3.5 mr-1.5" /> Spory ({data.disputes.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="orders" className="mt-4">
                {data.orders.length === 0 ? (
                  <Card className="p-10 text-center text-slate-500">Žádné objednávky.</Card>
                ) : (
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          <TableHead>Datum</TableHead>
                          <TableHead>kg</TableHead>
                          <TableHead>Cena</TableHead>
                          {isProvider && <TableHead>Vaše odměna</TableHead>}
                          <TableHead>Status</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.orders.map((o) => (
                          <TableRow key={o.id} data-testid={`ud-order-${o.id}`}>
                            <TableCell className="font-mono text-xs">{o.id.slice(0, 6)}</TableCell>
                            <TableCell className="text-xs">{formatDate(o.created_at)}</TableCell>
                            <TableCell>{o.kg}</TableCell>
                            <TableCell>{formatCzk(o.total_price)}</TableCell>
                            {isProvider && <TableCell className="text-emerald-700">{formatCzk(o.provider_payout)}</TableCell>}
                            <TableCell><StatusBadge status={o.status} /></TableCell>
                            <TableCell><Link to={`/order/${o.id}`} className="text-sky-600 text-sm hover:underline">Otevřít</Link></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                )}
              </TabsContent>

              {isProvider && (
                <TabsContent value="payouts" className="mt-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Card className="p-6 bg-emerald-50 border-emerald-200">
                      <div className="tiny-label text-emerald-700">Vyplaceno (dokončené)</div>
                      <div className="font-display text-3xl font-extrabold text-emerald-900 mt-2">
                        {formatCzk(data.payouts?.paid || 0)}
                      </div>
                      <div className="text-xs text-emerald-700 mt-1">{data.payouts?.count_paid || 0} objednávek</div>
                    </Card>
                    <Card className="p-6 bg-amber-50 border-amber-200">
                      <div className="tiny-label text-amber-700">Čeká na výplatu (v běhu)</div>
                      <div className="font-display text-3xl font-extrabold text-amber-900 mt-2">
                        {formatCzk(data.payouts?.pending || 0)}
                      </div>
                      <div className="text-xs text-amber-700 mt-1">{data.payouts?.count_pending || 0} objednávek</div>
                    </Card>
                  </div>

                  <Card className="mt-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Datum</TableHead>
                          <TableHead>Zákazník</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Odměna</TableHead>
                          <TableHead>Refundace</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.orders
                          .filter((o) => o.provider_id === data.id || o.status === "completed" || o.status === "accepted" || o.status === "picked_up" || o.status === "processing" || o.status === "delivered")
                          .map((o) => (
                          <TableRow key={o.id}>
                            <TableCell className="text-xs">{formatDate(o.created_at)}</TableCell>
                            <TableCell>{o.customer_name}</TableCell>
                            <TableCell><StatusBadge status={o.status} /></TableCell>
                            <TableCell className={o.status === "completed" ? "text-emerald-700 font-semibold" : "text-amber-700"}>
                              {formatCzk(o.provider_payout)}
                            </TableCell>
                            <TableCell>{o.refund_provider_part ? <span className="text-rose-600">−{formatCzk(o.refund_provider_part)}</span> : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                </TabsContent>
              )}

              <TabsContent value="disputes" className="mt-4">
                {data.disputes.length === 0 ? (
                  <Card className="p-10 text-center text-slate-500">Žádné spory.</Card>
                ) : (
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Datum</TableHead>
                          <TableHead>Důvod</TableHead>
                          <TableHead>Stav</TableHead>
                          <TableHead>Náhrada</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.disputes.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell className="text-xs">{formatDate(d.created_at)}</TableCell>
                            <TableCell>{d.category}</TableCell>
                            <TableCell><Badge variant="secondary">{d.status}</Badge></TableCell>
                            <TableCell>{d.refund_amount > 0 ? formatCzk(d.refund_amount) : "—"}</TableCell>
                            <TableCell><Link to={`/dispute/${d.id}`} className="text-sky-600 text-sm hover:underline">Otevřít</Link></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminUserDetail;
